import React from 'react';
import { useState } from 'react';
import { ChevronDown as Chevron } from 'lucide-react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { ImagePickField } from '../media/ImagePickField';

const Label = ({ children }) => <label className="text-xs font-medium text-stone-500 mb-1 block">{children}</label>;

const Group = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full px-5 py-4 flex items-center justify-between hover:bg-stone-50 text-left">
        <span className="font-medium text-stone-900">{title}</span>
        <Chevron className={`w-4 h-4 text-stone-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-5 pb-5 space-y-4">{children}</div>}
    </div>
  );
};

const CARD_FIELDS = [
  ['image', 'Card image URL'], ['city', 'City'], ['venue_type', 'Venue type'], ['rooms', 'Rooms'],
  ['stay_capacity', 'Stay capacity'], ['event_capacity', 'Event capacity'],
  ['starting_price_label', 'Starting price label'], ['feature', 'Distinctive feature']
];

const HeroMediaFields = ({ hero, updateHero }) => {
  if (hero?.media_type === 'video') {
    return (
      <div>
        <Label>Hero YouTube video URL</Label>
        <Input value={hero?.video_url ?? ''} onChange={e => updateHero('video_url', e.target.value)} data-testid="hero-video-input" />
      </div>
    );
  }
  return (
    <>
      <ImagePickField label="Hero background image" value={hero?.image_url} onChange={v => updateHero('image_url', v)} testId="hero-image-input" />
      <ImagePickField label="Mobile image (optional — desktop image is used on mobile if empty)" value={hero?.mobile_image_url} onChange={v => updateHero('mobile_image_url', v)} />
    </>
  );
};

export const BasicsTab = ({ page, update, updateHero, updateCard, updateContact }) => (
  <div className="space-y-4">
    <Group title="Page">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><Label>Page title</Label><Input value={page.name || ''} onChange={e => update({ name: e.target.value })} data-testid="builder-title-input" /></div>
        <div><Label>Slug (URL)</Label><Input value={page.slug || ''} onChange={e => update({ slug: e.target.value })} data-testid="builder-slug-input" /></div>
      </div>
      <p className="text-xs text-stone-400">Lowercase letters, numbers and dashes. Must be unique.</p>
    </Group>

    <Group title="Hero banner">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><Label>Overline (small label)</Label><Input value={page.hero?.overline ?? ''} placeholder={page.name} onChange={e => updateHero('overline', e.target.value)} /></div>
        <div>
          <Label>Background type</Label>
          <select
            value={page.hero?.media_type || 'image'}
            onChange={e => updateHero('media_type', e.target.value)}
            className="w-full h-10 rounded-md border border-stone-200 bg-white px-3 text-sm"
            data-testid="hero-media-type-select"
          >
            <option value="image">Image</option>
            <option value="video">YouTube video</option>
          </select>
        </div>
      </div>
      <div><Label>Headline</Label><Input value={page.hero?.headline ?? ''} onChange={e => updateHero('headline', e.target.value)} data-testid="hero-headline-input" /></div>
      <div><Label>Subheadline (optional)</Label><Textarea rows={2} value={page.hero?.subheadline ?? ''} onChange={e => updateHero('subheadline', e.target.value)} /></div>
      <HeroMediaFields hero={page.hero} updateHero={updateHero} />
      <div className="max-w-xs">
        <Label>Overlay darkness — keeps text on top readable</Label>
        <select
          value={page.hero?.overlay || 'medium'}
          onChange={e => updateHero('overlay', e.target.value)}
          className="w-full h-10 rounded-md border border-stone-200 bg-white px-3 text-sm"
        >
          <option value="none">None</option>
          <option value="light">Light</option>
          <option value="medium">Medium (default)</option>
          <option value="dark">Dark</option>
        </select>
      </div>
    </Group>

    <Group title="Contact (call & WhatsApp buttons)">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><Label>Phone</Label><Input value={page.contact?.phone ?? ''} onChange={e => updateContact('phone', e.target.value)} /></div>
        <div><Label>WhatsApp number</Label><Input value={page.contact?.whatsapp ?? ''} onChange={e => updateContact('whatsapp', e.target.value)} /></div>
      </div>
    </Group>

    <Group title="Venue card (homepage & browse listing)" defaultOpen={false}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {CARD_FIELDS.map(([k, l]) => (
          <div key={k}>
            {k === 'image'
              ? <ImagePickField label={l} value={page.card?.[k]} onChange={v => updateCard(k, v)} />
              : <><Label>{l}</Label><Input value={page.card?.[k] ?? ''} onChange={e => updateCard(k, e.target.value)} /></>}
          </div>
        ))}
      </div>
    </Group>
  </div>
);
